import React from "react";
import { Button } from "flowbite-react";
import { RecurringTransaction } from "../../../domain/types/RecurringTransaction";
import { formatCurrency } from "../../../utils/formatCurrency";
import {
  HiOutlineArrowUp,
  HiOutlineArrowDown,
  HiOutlineCalendar,
  HiOutlineCheck,
  HiOutlineClock,
} from "react-icons/hi";
import Tooltip from "../../common/Tooltip";

interface RecurringTransactionCardProps {
  transaction: RecurringTransaction;
  isCalendarConnected: boolean;
  isAddedToCalendar: boolean;
  onAddToCalendar: () => void;
  onReAddToCalendar: () => void;
  onClick: () => void;
}

export const RecurringTransactionCard: React.FC<
  RecurringTransactionCardProps
> = ({
  transaction,
  isCalendarConnected,
  isAddedToCalendar,
  onAddToCalendar,
  onReAddToCalendar,
  onClick,
}) => {
  return (
    <div
      className="group flex items-stretch bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 dark:border-gray-700 overflow-hidden cursor-pointer"
      onClick={onClick}
    >
      {/* Transaction Type Indicator */}
      <div
        className={`flex items-center px-4 border-r border-gray-100 dark:border-gray-700 ${
          transaction.stream_type === "inflow"
            ? "bg-green-50 dark:bg-green-900/20"
            : "bg-red-50 dark:bg-red-900/20"
        }`}
      >
        {transaction.stream_type === "inflow" ? (
          <HiOutlineArrowDown className="w-6 h-6 text-green-600 dark:text-green-400" />
        ) : (
          <HiOutlineArrowUp className="w-6 h-6 text-red-600 dark:text-red-400" />
        )}
      </div>

      {/* Main Content */}
      <div className="flex-grow flex items-center justify-between p-4">
        <div className="space-y-2">
          {/* Transaction Title and Amount */}
          <div className="flex items-center space-x-3">
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {transaction.description}
            </span>
            <span
              className={`text-lg font-bold ${
                transaction.stream_type === "inflow"
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {formatCurrency(
                transaction.last_amount.amount,
                transaction.last_amount.iso_currency_code ||
                  transaction.last_amount.unofficial_currency_code ||
                  "USD",
              )}
            </span>
          </div>

          {/* Merchant and Frequency */}
          <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-center">
              <HiOutlineClock className="w-4 h-4 mr-1" />
              {transaction.frequency.toLowerCase()}
            </div>
            {transaction.merchant_name && (
              <div>{transaction.merchant_name}</div>
            )}
          </div>

          {/* Next Date */}
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <HiOutlineCalendar className="w-4 h-4 mr-1" />
            Next:{" "}
            {new Date(transaction.predicted_next_date).toLocaleDateString()}
          </div>
        </div>

        {/* Actions */}
        <div
          className="flex items-center space-x-4"
          onClick={(e) => e.stopPropagation()}
        >
          {isCalendarConnected ? (
            isAddedToCalendar ? (
              <Button
                onClick={onReAddToCalendar}
                size="sm"
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <HiOutlineCheck className="w-4 h-4 mr-2" />
                Added to Calendar
              </Button>
            ) : (
              <Button
                onClick={onAddToCalendar}
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <HiOutlineCalendar className="w-4 h-4 mr-2" />
                Add to Calendar
              </Button>
            )
          ) : (
            <Tooltip content="Please connect your Google Calendar in your account settings.">
              <Button
                disabled
                size="sm"
                className="bg-gray-400 text-white cursor-not-allowed"
              >
                <HiOutlineCalendar className="w-4 h-4 mr-2" />
                Add to Calendar
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
